import { Component, ElementRef, Input, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { CreditorInfo } from '../../interfaces/creditorInfo.interface';
import { CreditorGroupMemberInfo } from '../../interfaces/creditorGroupMemberInfo.interface';
import { CreditorClaimProofOfDebtSummary } from '../../interfaces/creditorClaimProofOfDebtSummary.interface';

export interface TransferResult {
  transfers: { gkCaseId: string; toUserId: string }[];
  leave: boolean;
}

@Component({
    selector: 'app-creditor-group-transfer-modal',
    templateUrl: './creditor-group-transfer-modal.component.html',
    styleUrls: ['./creditor-group-transfer-modal.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class CreditorGroupTransferModalComponent implements OnInit {
  @Input() public cases: CreditorInfo[] = [];
  @Input() public members: CreditorGroupMemberInfo[] = [];
  @Input() public claimsMap: Record<string, CreditorClaimProofOfDebtSummary[]> = {};

  @ViewChild('modalBody') private modalBodyRef: ElementRef<HTMLElement>;

  public isScrollable = false;
  public isAtBottom = false;
  public globalRecipient = '';

  private _isLoading = true;

  @Input() set isLoading(value: boolean) {
    this._isLoading = value;
    if (!value) {
      setTimeout(() => this.checkScroll(), 0);
    }
  }

  get isLoading(): boolean {
    return this._isLoading;
  }

  public readonly claimsPreviewCount = 5;
  private expandedClaims = new Set<string>();

  public get transferSummaryKey(): string {
    return this.cases.length === 1 ? 'group.transfer-summary-one' : 'group.transfer-summary-other';
  }

  public getClaimLabel(claim: CreditorClaimProofOfDebtSummary): string {
    if (claim.isPreRequest) return 'group.transfer-claim-pre-request';
    if (claim.isLateRequest) return 'group.transfer-claim-late-proof';
    if (claim.isDeclined) return 'group.transfer-claim-declined';
    return 'group.transfer-claim-proof-of-debt';
  }

  public isClaimsExpanded(caseId: string): boolean {
    return this.expandedClaims.has(caseId);
  }

  public toggleClaims(caseId: string): void {
    if (this.expandedClaims.has(caseId)) this.expandedClaims.delete(caseId);
    else this.expandedClaims.add(caseId);
  }

  public getVisibleClaims(caseId: string): CreditorClaimProofOfDebtSummary[] {
    const claims = this.claimsMap[caseId] ?? [];
    return this.isClaimsExpanded(caseId) ? claims : claims.slice(0, this.claimsPreviewCount);
  }

  public getHiddenClaimCount(caseId: string): number {
    return Math.max(0, (this.claimsMap[caseId]?.length ?? 0) - this.claimsPreviewCount);
  }

  public transferSelections: { [gkCaseId: string]: string } = {};

  constructor(private activeModal: NgbActiveModal) {}

  ngOnInit(): void {
    for (const creditorCase of this.cases) {
      this.transferSelections[creditorCase.caseSummary.caseId] = '';
    }
  }

  public onGlobalRecipientChange(): void {
    for (const creditorCase of this.cases) {
      this.transferSelections[creditorCase.caseSummary.caseId] = this.globalRecipient;
    }
  }

  public checkScroll(): void {
    const modalBody = this.modalBodyRef?.nativeElement;
    if (!modalBody) { this.isScrollable = false; return; }
    this.isScrollable = modalBody.scrollHeight > modalBody.clientHeight + 10;
    this.isAtBottom = modalBody.scrollTop + modalBody.clientHeight >= modalBody.scrollHeight - 20;
  }

  public scrollToggle(): void {
    const modalBody = this.modalBodyRef?.nativeElement;
    if (!modalBody) return;
    modalBody.scrollTo({ top: this.isAtBottom ? 0 : modalBody.scrollHeight, behavior: 'smooth' });
  }

  public confirmAndLeave(): void {
    const transfers = Object.entries(this.transferSelections)
      .filter(([gkCaseId, toUserId]) => !!toUserId)
      .map(([gkCaseId, toUserId]) => ({ gkCaseId, toUserId }));
    this.activeModal.close({ transfers, leave: true } as TransferResult);
  }

  public leaveWithoutTransfer(): void {
    this.activeModal.close({ transfers: [], leave: true } as TransferResult);
  }

  public cancel(): void {
    this.activeModal.dismiss();
  }
}
